import { whatsAppConversationsPageSchema, whatsAppUnreadCountSchema, type WhatsAppConversationsPage, type WhatsAppUnreadCount } from "@gioia365/contracts";
import { infiniteQueryOptions, queryOptions, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiRequest, ApiRequestError } from "../api/http";
import { leadKeys } from "../leads/lead-queries";
import { commercialViewOf, tabKey, type TabContext } from "../session/tab-context";
import { DUE_NOW_REFRESH_MS } from "../tasks/tasks-queries";
import { conversationKeys } from "./conversation-keys";
import { CONVERSATIONS_PAGE_SIZE } from "./conversation-queries";
import type { WhatsAppConversationsRequest } from "./conversation-search";

// Le letture e le scritture della scheda «WhatsApp» di «Conversazioni» (piano M6, R3, passo 12b; rotte dei passi 12a e 11c, contratti
// in `packages/contracts/src/conversations.ts`):
// - `GET /api/conversations/whatsapp` a pagine con il cursore, con periodo, «Di chi», «Solo da leggere» e ricerca dell'indirizzo;
// - `GET /api/whatsapp/unread-count`, il numero da leggere accanto a «Conversazioni» nel menu e sul pulsante della scheda (D24): si
//   rilegge come il banner dei richiami di M5 (ogni 60 secondi, al ritorno sulla finestra e dopo ogni lettura), senza canali nuovi;
// - «Segna come letto» (`POST /api/whatsapp/messages/:messageId/read`, anche da «Rispondi» e dalla Timeline) e «Segna tutti come
//   letti» (`POST /api/whatsapp/messages/read-all`, con gli stessi filtri della lista nella query string e `until`, l'ora del server
//   della prima pagina che chi legge ha davanti, `readUntil`): dopo, la lista, il numero da leggere e le Timeline si rileggono.
// Anche la lista si rilegge ogni 60 secondi mentre è aperta: le risposte dei Lead arrivano da sole.

/** Ogni quanto il numero da leggere e la lista aperta si rileggono: come il banner dei richiami (M5, D14). */
export const WHATSAPP_REFRESH_MS = DUE_NOW_REFRESH_MS;

/** I filtri della scheda nella query string, con i nomi del server: la lista e «Segna tutti come letti» li mandano uguali. */
function filterParams(query: WhatsAppConversationsRequest): URLSearchParams {
  const params = new URLSearchParams({ period: query.period });
  if (query.from !== undefined) params.set("from", query.from);
  if (query.to !== undefined) params.set("to", query.to);
  if (query.who !== undefined) params.set("who", query.who);
  if (query.unread !== undefined) params.set("unread", String(query.unread));
  if (query.q !== undefined && query.q !== "") params.set("q", query.q);
  return params;
}

function listPath(query: WhatsAppConversationsRequest, cursor: string | null): string {
  const params = filterParams(query);
  if (cursor !== null) params.set("cursor", cursor);
  params.set("limit", String(CONVERSATIONS_PAGE_SIZE));
  return `/api/conversations/whatsapp?${params.toString()}`;
}

/** Le pagine dei WhatsApp: la successiva dal cursore della precedente, finché il server ne dà uno. */
export function whatsAppPagesQuery(tab: TabContext, query: WhatsAppConversationsRequest, view: string | null) {
  return infiniteQueryOptions({
    queryKey: conversationKeys.whatsapp(tabKey(tab), view, query),
    queryFn: ({ signal, pageParam }) => apiRequest("GET", listPath(query, pageParam), { schema: whatsAppConversationsPageSchema, tab, signal }),
    initialPageParam: null as string | null,
    getNextPageParam: (last: WhatsAppConversationsPage) => last.nextCursor ?? undefined,
    staleTime: 30_000,
    refetchInterval: WHATSAPP_REFRESH_MS,
  });
}

/**
 * Il numero da leggere nella vista della scheda (nella «Guarda come…» quello della persona osservata). Un server senza la rotta (la
 * demo locale di prima di M6 R3) risponde il `404 not_found` di ogni rotta che non c'è: nessun numero, senza errore.
 */
export function whatsAppUnreadQuery(tab: TabContext) {
  return queryOptions({
    queryKey: conversationKeys.whatsappUnread(tabKey(tab), commercialViewOf(tab)),
    queryFn: async ({ signal }): Promise<WhatsAppUnreadCount> => {
      try {
        return await apiRequest("GET", "/api/whatsapp/unread-count", { schema: whatsAppUnreadCountSchema, tab, signal });
      } catch (error) {
        if (error instanceof ApiRequestError && error.status === 404 && error.code === "not_found") return { count: 0 };
        throw error;
      }
    },
    refetchInterval: WHATSAPP_REFRESH_MS,
  });
}

/**
 * Dopo una lettura o una risposta: la lista dei WhatsApp e il numero da leggere (il menu, il pulsante della scheda) in ogni vista, e
 * le Timeline del Lead (di tutti i Lead della scheda, quando non si sa quali: «Segna tutti come letti»).
 */
export function refreshAfterWhatsAppRead(queryClient: QueryClient, tab: TabContext, leadId: string | null, view: string | null): void {
  const tabPart = tabKey(tab);
  void queryClient.invalidateQueries({ queryKey: conversationKeys.whatsappLists(tabPart) });
  void queryClient.invalidateQueries({ queryKey: conversationKeys.whatsappUnreadAll(tabPart) });
  void queryClient.invalidateQueries({ queryKey: leadId === null ? leadKeys.allTimelines(tabPart) : leadKeys.timelines(tabPart, leadId, view) });
}

/** «Segna come letto» (anche «Rispondi»): `204` anche se era già letto; un messaggio che non si vede più è `404`. Dopo, si rilegge. */
export function useMarkWhatsAppRead(tab: TabContext, view: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId }: { messageId: string; leadId: string | null }) =>
      apiRequest("POST", `/api/whatsapp/messages/${encodeURIComponent(messageId)}/read`, { schema: z.undefined(), tab }),
    onSettled: (_result, _error, { leadId }) => refreshAfterWhatsAppRead(queryClient, tab, leadId, view),
  });
}

/**
 * «Segna tutti come letti»: quelli da leggere con i filtri della lista arrivati fino a `until` (il `readUntil` della prima pagina).
 * Dopo, si rilegge.
 */
export function useMarkAllWhatsAppRead(tab: TabContext, view: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ query, until }: { query: WhatsAppConversationsRequest; until: string | null }) => {
      const params = filterParams(query);
      if (until !== null) params.set("until", until);
      return apiRequest("POST", `/api/whatsapp/messages/read-all?${params.toString()}`, { schema: z.undefined(), tab });
    },
    onSettled: () => refreshAfterWhatsAppRead(queryClient, tab, null, view),
  });
}
